import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import PublicIcon from '@mui/icons-material/Public';
import { getUserGroupColour, getUserGroupInitials } from './userGroupConstants';

// Atomic mark used by the User Group chip (sm, 20px) and the switcher
// dropdown rows (md, 32px). Renders a favicon image when supplied, falls
// back to deterministic initials in a coloured square on null/error.
const SIZES = {
  sm: { box: 20, radius: 4, font: 10 },
  md: { box: 32, radius: 6, font: 13 }
};

const UserGroupMark = ({ userGroup, size = 'sm' }) => {
  const dims = SIZES[size] || SIZES.sm;
  const id = userGroup?._id || userGroup?.id || '';
  const name = userGroup?.label || userGroup?.name || '';
  const faviconUrl = userGroup?.faviconUrl || null;
  const [imgFailed, setImgFailed] = useState(false);

  // Reset the error flag if the URL changes (e.g., after a re-fetch).
  useEffect(() => {
    setImgFailed(false);
  }, [faviconUrl]);

  const showImage = faviconUrl && !imgFailed;

  // A System Admin is in no clinic group (TGCLOUD-1858): a globe, not the
  // "?" the initials fall back to for a group with no name.
  if (userGroup?.global) {
    return (
      <Box
        aria-hidden="true"
        data-testid="user-group-mark-global"
        sx={{
          width: dims.box,
          height: dims.box,
          borderRadius: `${dims.radius}px`,
          bgcolor: '#4B5563',
          color: '#fff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <PublicIcon sx={{ fontSize: Math.round(dims.box * 0.7) }} />
      </Box>
    );
  }

  if (showImage) {
    return (
      <Box
        component="img"
        src={faviconUrl}
        alt=""
        loading="lazy"
        onError={() => setImgFailed(true)}
        sx={{
          width: dims.box,
          height: dims.box,
          borderRadius: `${dims.radius}px`,
          objectFit: 'cover',
          flexShrink: 0,
          display: 'block'
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: dims.box,
        height: dims.box,
        borderRadius: `${dims.radius}px`,
        bgcolor: getUserGroupColour(id),
        color: '#fff',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: dims.font,
        fontWeight: 600,
        letterSpacing: '0.02em',
        lineHeight: 1,
        userSelect: 'none'
      }}
    >
      {getUserGroupInitials(name)}
    </Box>
  );
};

export default UserGroupMark;
